import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';
import RichText, { richToHtml } from './RichText';
import type { InlineTextTarget } from './inlineText';
import { useWorkspaceDraftGuard } from './useWorkspaceDraftGuard';
import './inline-text.css';

interface Props {
  target: InlineTextTarget;
  viewport: HTMLElement;
  onApply: (target: InlineTextTarget, value: string) => Promise<void | boolean>;
  onClose: () => void;
}

/** Replace the selected SVG text visually, inside the canvas. The original
 * SVG and spec stay intact until Apply succeeds; export still uses the renderer. */
export default function InlineTextEditor({ target, viewport, onApply, onClose }: Props) {
  const [draft, setDraft] = useState(target.value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const field = useRef<HTMLDivElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);
  const [layout, setLayout] = useState<React.CSSProperties>({ visibility: 'hidden' });
  const [textStyle, setTextStyle] = useState<React.CSSProperties>({});
  useWorkspaceDraftGuard(() => ({ dirty: draft !== target.value, busy }));

  useLayoutEffect(() => {
    const place = () => {
      const node = target.nodes[0];
      if (!node.isConnected) return;
      const rects = target.nodes.map(n => n.getBoundingClientRect());
      const bounds = viewport.getBoundingClientRect();
      // Browser focus/scrollIntoView can scroll even an overflow:hidden
      // canvas. Keep the editing layer pinned to its visible viewport.
      if (layer.current) layer.current.style.transform = `translate(${viewport.scrollLeft}px, ${viewport.scrollTop}px)`;
      const left = Math.min(...rects.map(r => r.left)), right = Math.max(...rects.map(r => r.right));
      const top = Math.min(...rects.map(r => r.top));
      const style = window.getComputedStyle(node);
      const matrix = node.getScreenCTM?.();
      const scale = matrix ? Math.hypot(matrix.a, matrix.b) : 1;
      const size = parseFloat(style.fontSize || '18') * scale;
      const lineHeight = rects.length > 1 && rects[1].top > rects[0].top
        ? rects[1].top - rects[0].top : size * 1.34;
      const anchor = node.getAttribute('text-anchor') || style.textAnchor;
      const align = anchor === 'middle' ? 'center' : anchor === 'end' ? 'right' : 'left';
      let width = Math.max(right - left + 2, size * 2);
      // Short phase labels need room for the replacement while typing.
      if (target.path === 'meta.phase_label') width = Math.max(width, 240);
      let x = left - bounds.left - 1;
      // Centre-aligned labels use the space inside their own box. Text keeps
      // its original centre, including while typing a longer label.
      if (align === 'center') {
        const group = node.closest('[data-path]');
        const box = Array.from(group?.querySelectorAll('rect') || []).map(n => n.getBoundingClientRect()).find(r =>
          r.left <= left && r.right >= right && r.top <= top && r.bottom >= rects[0].bottom);
        if (box) width = Math.max(width, box.width - 20 * scale);
        x = (left + right) / 2 - bounds.left - width / 2;
      } else if (align === 'right') x = right - bounds.left - width;
      // The editor is part of the canvas and clips with it. On a narrow
      // screen reflow only the editing field, keeping the selected text visible.
      const view = window.visualViewport;
      const visibleBottom = Math.min(bounds.bottom, (view?.offsetTop || 0) + (view?.height || window.innerHeight));
      const visibleTop = Math.max(bounds.top, view?.offsetTop || 0);
      const maxWidth = Math.max(40, bounds.width - 32);
      width = Math.min(width, maxWidth);
      if (align === 'left' && x >= 16 && x < bounds.width - 56) width = Math.min(width, bounds.width - x - 16);
      x = Math.max(16, Math.min(x, bounds.width - width - 16));
      const y = Math.max(visibleTop - bounds.top + 68, Math.min(top - bounds.top, visibleBottom - bounds.top - lineHeight - 20));
      const maxHeight = Math.max(lineHeight, visibleBottom - bounds.top - y - 16);
      setLayout({ left: x, top: y, width, maxHeight });
      const letterSpacing = parseFloat(style.letterSpacing);
      setTextStyle({ fontFamily: style.fontFamily, fontSize: size, fontWeight: style.fontWeight,
        fontStyle: style.fontStyle, letterSpacing: Number.isFinite(letterSpacing) ? letterSpacing * scale : style.letterSpacing, lineHeight: `${lineHeight}px`,
        color: style.fill === 'none' ? undefined : style.fill, textAlign: align });
    };
    place();
    // Visibility preserves the SVG geometry: panning, selection and cancel
    // retain the exact original bounds. Never hide neighbouring text or bullets.
    const previous = target.nodes.map(n => n.style.visibility);
    target.nodes.forEach(n => { n.style.visibility = 'hidden'; n.classList.add('arch-inline-source'); });
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    resize?.observe(viewport);
    const stage = viewport.querySelector('.arch-svg-stage');
    const transforms = new MutationObserver(place);
    if (stage) transforms.observe(stage, { attributes: true, attributeFilter: ['style'] });
    window.addEventListener('resize', place);
    viewport.addEventListener('scroll', place);
    window.visualViewport?.addEventListener('resize', place);
    window.visualViewport?.addEventListener('scroll', place);
    return () => {
      target.nodes.forEach((n, i) => { n.style.visibility = previous[i]; n.classList.remove('arch-inline-source'); });
      resize?.disconnect(); transforms.disconnect();
      window.removeEventListener('resize', place); viewport.removeEventListener('scroll', place);
      window.visualViewport?.removeEventListener('resize', place); window.visualViewport?.removeEventListener('scroll', place);
    };
  }, [target, viewport]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const frame = requestAnimationFrame(() => field.current?.querySelector<HTMLElement>('textarea, [contenteditable]')?.focus({ preventScroll: true }));
    return () => { cancelAnimationFrame(frame); previous?.focus?.({ preventScroll: true }); };
  }, []);

  useLayoutEffect(() => {
    const textarea = field.current?.querySelector('textarea');
    if (textarea) { textarea.style.height = '0px'; textarea.style.height = textarea.scrollHeight + 'px'; }
    const rich = field.current?.querySelector<HTMLElement>('[contenteditable]');
    if (rich) rich.contentEditable = busy ? 'false' : 'true';
  }, [draft, busy, textStyle, layout.width]);

  const apply = async () => {
    if (busyRef.current) return;
    if (draft === target.value) { onClose(); return; }
    const content = document.createElement('div'); content.innerHTML = richToHtml(draft.slice(target.prefix.length));
    if (!target.allowEmpty && !content.textContent?.trim()) { setError('Text cannot be empty.'); return; }
    if (target.maxLength && draft.trim().length > target.maxLength) { setError(`Use up to ${target.maxLength} characters.`); return; }
    busyRef.current = true; setBusy(true); setError('');
    try { if (await onApply(target, draft) !== false) onClose(); }
    catch (err: any) { setError(err.message || 'Could not save. Your text is still here.'); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const close = () => { if (!busyRef.current) onClose(); };
  const keyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey || !target.rich)) { e.preventDefault(); void apply(); }
  };
  const plain = document.createElement('div'); plain.innerHTML = richToHtml(target.value.slice(target.prefix.length));
  return createPortal(<div ref={layer} className="arch-inline-layer" onKeyDown={keyDown}>
    <div className="arch-inline-tools" role="toolbar" aria-label="Text editing" onPointerDown={e => e.stopPropagation()}>
      <span className="arch-inline-label">Editing text</span>
      <span className="arch-inline-shortcut">Esc to cancel</span>
      <button type="button" onClick={close} disabled={busy}><X size={15}/>Cancel</button>
      <button type="button" className="arch-inline-apply" onClick={() => void apply()} disabled={busy}><Check size={16}/>{busy ? 'Saving…' : 'Save'}</button>
    </div>
    {error ? <div className="arch-inline-error" role="alert">{error}</div> : null}
    <div ref={field} className="arch-inline-field" style={{ ...layout, ...textStyle }} role="group" aria-label="Edit architecture text" aria-busy={busy} onPointerDown={e => e.stopPropagation()} onWheel={e => e.stopPropagation()}>
      {target.rich ? <RichText value={target.value.slice(target.prefix.length)} onChange={v => setDraft(target.prefix + v)} onReady={el => { el.setAttribute('role', 'textbox'); el.setAttribute('aria-label', 'Architecture text'); }} />
        : <textarea aria-label="Architecture text" disabled={busy} maxLength={target.maxLength} defaultValue={target.literal ? target.value : plain.textContent || ''} rows={1} onChange={e => {
          const value = e.target.value.replace(/\r?\n/g, ' ');
          setDraft(target.prefix + (target.literal ? value : value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')));
        }} />}
    </div>
  </div>, viewport);
}
